import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import useInstituteData, { percentOf, isPass, fmtDate } from "../Useinstitutedata";
import InstituteSidebar from "./Institutesidebar";
import InstituteTopbar from "./Institutetopbar";


/* ---------- helpers ---------- */
const COLORS = ["#5b8def", "#9b6cf0", "#2fbf9b", "#f6a54b"];
const courseName = (c) => (typeof c === "string" ? c : c?.name || c?.title || "");
const firstCourse = (s) => courseName((s.courses || [])[0]) || "Others";
const monthKey = (d) => (d || "").slice(0, 7);
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const bookingDate = (p) => p.examDate || p.scheduledDate || p.date || p.scheduledAt || "";
const todayStr = () => new Date().toISOString().slice(0, 10);
const initials = (n = "") => (n.trim()[0] || "?").toUpperCase();

function readInstitute(fromHook) {
  if (fromHook) return fromHook;
  for (const k of ["institute", "instituteUser", "instituteInfo"]) {
    try {
      const v = JSON.parse(localStorage.getItem(k) || sessionStorage.getItem(k) || "null");
      if (v) return v;
    } catch { /* ignore */ }
  }
  return {};
}

/* ---------- small SVG charts (no extra dependencies) ---------- */
function Spark({ values, color }) {
  const max = Math.max(...values, 1);
  const pts = values.map((v, i) => `${(i / (values.length - 1)) * 70},${26 - (v / max) * 22}`).join(" ");
  return (
    <svg className="idb-spark" viewBox="0 0 70 28" aria-hidden="true">
      <polyline points={pts} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function Donut({ parts, total, label }) {
  const R = 52, C = 2 * Math.PI * R;
  let offset = 0;
  return (
    <svg className="idb-donut" viewBox="0 0 140 140" role="img" aria-label={label}>
      <circle cx="70" cy="70" r={R} fill="none" stroke="#eef1f6" strokeWidth="22" />
      {total > 0 &&
        parts.map((p) => {
          const len = (p.value / total) * C;
          const el = (
            <circle key={p.name} cx="70" cy="70" r={R} fill="none" stroke={p.color} strokeWidth="22"
              strokeDasharray={`${len} ${C - len}`} strokeDashoffset={-offset} transform="rotate(-90 70 70)" />
          );
          offset += len;
          return el;
        })}
      <text x="70" y="68" textAnchor="middle" className="idb-donut-num">{total}</text>
      <text x="70" y="85" textAnchor="middle" className="idb-donut-lbl">{label}</text>
    </svg>
  );
}

function GrowthChart({ data }) {
  const [hover, setHover] = useState(data.length - 1);
  const W = 560, H = 200, L = 34, B = 26, T = 14;
  const max = Math.max(5, Math.ceil(Math.max(...data.map((d) => d.value)) / 5) * 5);
  const x = (i) => L + (i / (data.length - 1)) * (W - L - 10);
  const y = (v) => T + (1 - v / max) * (H - T - B);
  const line = data.map((d, i) => `${i ? "L" : "M"}${x(i)},${y(d.value)}`).join(" ");
  const area = `${line} L${x(data.length - 1)},${H - B} L${x(0)},${H - B} Z`;
  const ticks = [0, max / 3, (2 * max) / 3, max].map(Math.round);
  const h = data[hover];
  return (
    <div className="idb-growth">
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Monthly student growth">
        <defs>
          <linearGradient id="idbGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#5b8def" stopOpacity=".25" />
            <stop offset="1" stopColor="#5b8def" stopOpacity="0" />
          </linearGradient>
        </defs>
        {ticks.map((t) => (
          <g key={t}>
            <line x1={L} x2={W - 10} y1={y(t)} y2={y(t)} stroke="#eef1f6" />
            <text x={L - 8} y={y(t) + 4} textAnchor="end" className="idb-axis">{t}</text>
          </g>
        ))}
        <path d={area} fill="url(#idbGrad)" />
        <path d={line} fill="none" stroke="#5b8def" strokeWidth="2.2" />
        {data.map((d, i) => (
          <g key={d.key} onMouseEnter={() => setHover(i)} style={{ cursor: "pointer" }}>
            <rect x={x(i) - 20} y={T} width="40" height={H - T - B} fill="transparent" />
            <circle cx={x(i)} cy={y(d.value)} r={i === hover ? 5 : 3.5} fill="#fff" stroke="#5b8def" strokeWidth="2" />
            <text x={x(i)} y={H - 6} textAnchor="middle" className="idb-axis">{d.label}</text>
          </g>
        ))}
      </svg>
      <div className="idb-tip" style={{ left: `${(x(hover) / W) * 100}%`, top: `${(y(h.value) / H) * 100}%` }}>
        {h.label}<br /><b>{h.value} Students</b>
      </div>
    </div>
  );
}

function Empty({ text, sub }) {
  return (
    <div className="idb-empty">
      <i className="bi bi-file-earmark-text" />
      <strong>{text}</strong>
      <span>{sub}</span>
    </div>
  );
}

/* ---------- page ---------- */
export default function InstituteDashboard() {
  const navigate = useNavigate();
  const { students, purchases, scores, loading, error, institute: hookInstitute } = useInstituteData();
  const [courseFilter, setCourseFilter] = useState("All Courses");
  const institute = readInstitute(hookInstitute);

  const d = useMemo(() => {
    const passed = scores.filter((s) => isPass(s.result)).length;
    const avg = scores.length ? Math.round(scores.reduce((a, s) => a + percentOf(s), 0) / scores.length) : null;

    // last 6 months, cumulative registrations
    const now = new Date();
    const months = Array.from({ length: 6 }, (_, i) => {
      const dt = new Date(now.getFullYear(), now.getMonth() - (5 - i), 1);
      const key = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}`;
      return { key, label: MONTHS[dt.getMonth()] };
    });
    const growth = months.map((m) => ({
      ...m,
      value: students.filter((s) => monthKey(s.registeredAt) && monthKey(s.registeredAt) <= m.key).length,
    }));

    // students per course (top 3 + Others)
    const counts = {};
    students.forEach((s) => { const c = firstCourse(s); counts[c] = (counts[c] || 0) + 1; });
    const sorted = Object.entries(counts).filter(([n]) => n !== "Others").sort((a, b) => b[1] - a[1]);
    const top = sorted.slice(0, 3);
    const others = students.length - top.reduce((a, [, v]) => a + v, 0);
    const byCourse = [...top.map(([name, value]) => ({ name, value })), ...(others > 0 ? [{ name: "Others", value: others }] : [])]
      .map((p, i) => ({ ...p, color: COLORS[i % COLORS.length] }));

    // exam status
    const t = todayStr();
    const scoredKeys = new Set(scores.map((s) => `${s.userId}|${s.courseName}`));
    const pending = purchases.filter((p) => !scoredKeys.has(`${p.userId}|${p.courseName}`));
    const upcoming = pending.filter((p) => bookingDate(p).slice(0, 10) > t).length;
    const ongoing = pending.length - upcoming;
    const completed = scores.length;
    const examTotal = upcoming + ongoing + completed;

    // performance per course
    let perf;
    if (scores.length) {
      const g = {};
      scores.forEach((s) => { (g[s.courseName] ||= []).push(percentOf(s)); });
      perf = { unit: "%", bars: Object.entries(g).slice(0, 4).map(([name, v], i) => ({ name, value: Math.round(v.reduce((a, b) => a + b, 0) / v.length), color: COLORS[i % 4] })) };
    } else {
      perf = { unit: "", bars: byCourse };
    }

    const weekAgo = new Date(Date.now() - 7 * 864e5).toISOString().slice(0, 10);
    const newThisWeek = students.filter((s) => (s.registeredAt || "") >= weekAgo).length;

    return { passed, avg, growth, byCourse, upcoming, ongoing, completed, examTotal, perf, newThisWeek };
  }, [students, purchases, scores]);

  const active = students.filter((s) => s.status !== "Inactive").length;
  const spark = d.growth.map((g) => g.value);
  const weekNote = d.newThisWeek > 0
    ? <span className="idb-up"><i className="bi bi-arrow-up-right" /> +{d.newThisWeek} this week</span>
    : <span className="idb-flat">— no change</span>;

  const cards = [
    { label: "Total Students", value: students.length, icon: "bi-people", tone: "blue", color: "#5b8def", note: weekNote },
    { label: "Active Students", value: active, icon: "bi-person-check", tone: "green", color: "#2fbf9b", note: weekNote },
    { label: "Exams Booked", value: purchases.length, icon: "bi-calendar-check", tone: "purple", color: "#9b6cf0", note: <span className="idb-flat">— no change</span> },
    { label: "Exams Attempted", value: scores.length, icon: "bi-journal-check", tone: "orange", color: "#f6a54b", note: <span className="idb-flat">— no change</span> },
    { label: "Passed", value: d.passed, icon: "bi-check-circle", tone: "pink", color: "#ee6b9a", note: <span className="idb-flat">— no change</span> },
    { label: "Average Score", value: d.avg == null ? "—" : `${d.avg}%`, icon: "bi-graph-up", tone: "teal", color: "#2fbf9b", note: <span className="idb-flat">{d.avg == null ? "— no data" : "overall"}</span> },
  ];

  
const nameOf = (id) =>
  students.find(
    (student) =>
      String(student.userId) === String(id) ||
      String(student.id) === String(id)
  )?.fullName || "—";

  const recentStudents = [...students].sort((a, b) => (b.registeredAt || "").localeCompare(a.registeredAt || "")).slice(0, 5);
  const recentScores = [...scores].sort((a, b) => (b.examDate || "").localeCompare(a.examDate || "")).slice(0, 5);

  const courseOptions = ["All Courses", ...d.byCourse.map((c) => c.name)];
  const shownCourses = courseFilter === "All Courses" ? d.byCourse : d.byCourse.filter((c) => c.name === courseFilter);
  const shownTotal = shownCourses.reduce((a, c) => a + c.value, 0);

  const status = [
    { name: "Upcoming", value: d.upcoming, color: "#5b8def" },
    { name: "Ongoing", value: d.ongoing, color: "#9b6cf0" },
    { name: "Completed", value: d.completed, color: "#2fbf9b" },
  ];
  const pct = (v, t) => (t ? Math.round((v / t) * 100) : 0);
  const perfMax = Math.max(10, Math.ceil(Math.max(...d.perf.bars.map((b) => b.value), 1) / 2) * 2);

  return (
    <div className="admin-page-full d-flex">
      <InstituteSidebar />
      <div className="admin-content">
        <InstituteTopbar />

        {/* Institute header */}
        <div className="idb-head">
          <div className="idb-head-left">
            <div className="idb-head-icon"><i className="bi bi-bank" /></div>
            <div>
              <h2 className="idb-head-name">{institute.name || "Institute"}</h2>
              <div className="idb-head-mail">{institute.email || ""}</div>
            </div>
          </div>
          <div className="idb-date">
            <i className="bi bi-calendar3" />
            {new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" })}
          </div>
        </div>

        {error && <div className="alert alert-warning py-2 small">{error}</div>}
        {loading && <p className="text-muted">Loading dashboard...</p>}

        {!loading && (
          <>
            {/* Stat cards */}
            <div className="idb-stats">
              {cards.map((c) => (
                <div className="idb-stat" key={c.label}>
                  <div className="idb-stat-top">
                    <div className={`idb-stat-icon ${c.tone}`}><i className={`bi ${c.icon}`} /></div>
                    <Spark values={spark.length > 1 ? spark : [0, 0]} color={c.color} />
                  </div>
                  <div className="idb-stat-value">{c.value}</div>
                  <div className="idb-stat-label">{c.label}</div>
                  <div className="idb-stat-note">{c.note}</div>
                </div>
              ))}
            </div>

            {/* Row 1 */}
            <div className="idb-grid idb-grid-3">
              <section className="idb-card">
                <div className="idb-card-head">
                  <h3><i className="bi bi-people" />Students by Course</h3>
                  <select className="idb-select" value={courseFilter} onChange={(e) => setCourseFilter(e.target.value)}>
                    {courseOptions.map((o) => <option key={o}>{o}</option>)}
                  </select>
                </div>
                <div className="idb-course">
                  <Donut parts={shownCourses} total={shownTotal} label="Total Students" />
                  <ul className="idb-legend">
                    {shownCourses.map((c) => (
                      <li key={c.name}>
                        <span className="idb-dot" style={{ background: c.color }} />
                        <span className="idb-legend-name">{c.name}</span>
                        <span>{c.value}</span>
                        <span className="idb-legend-pct">{pct(c.value, shownTotal)}%</span>
                      </li>
                    ))}
                    {shownCourses.length === 0 && <li className="text-muted small">No students yet.</li>}
                  </ul>
                </div>
              </section>

              <section className="idb-card">
                <div className="idb-card-head"><h3><i className="bi bi-graph-up" />Monthly Student Growth</h3></div>
                <GrowthChart data={d.growth} />
              </section>

              <section className="idb-card">
                <div className="idb-card-head"><h3><i className="bi bi-pie-chart" />Exam Status</h3></div>
                <div className="idb-course">
                  <Donut parts={status} total={d.examTotal} label="Total Exams" />
                  <ul className="idb-legend">
                    {status.map((s) => (
                      <li key={s.name}>
                        <span className="idb-dot" style={{ background: s.color }} />
                        <span className="idb-legend-name">{s.name}</span>
                        <span className="idb-legend-pct">{s.value} ({pct(s.value, d.examTotal)}%)</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </section>
            </div>

            {/* Row 2 */}
            <div className="idb-grid idb-grid-3b">
              <section className="idb-card">
                <div className="idb-card-head">
                  <h3><i className="bi bi-person-lines-fill" />Recently Registered Students</h3>
                  <button className="idb-link" onClick={() => navigate("/institute/students")}>View All <i className="bi bi-chevron-right" /></button>
                </div>
                {recentStudents.length === 0 ? (
                  <p className="text-muted small mb-0">No students yet. Add one manually or import a PDF.</p>
                ) : (
                  <div className="table-responsive">
                    <table className="table align-middle idb-table mb-0">
                      <thead><tr><th>Name</th><th>Email</th><th>Registered</th></tr></thead>
                      <tbody>
                        {recentStudents.map((s, i) => (
                          <tr key={s.id}>
                            <td>
                              <span className="idb-avatar" style={{ background: COLORS[i % 4] }}>{initials(s.fullName)}</span>
                              <span className="fw-semibold">{s.fullName}</span>
                            </td>
                            <td className="text-muted">{s.email}</td>
                            <td className="text-muted">{fmtDate(s.registeredAt)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>

              <section className="idb-card">
                <div className="idb-card-head"><h3><i className="bi bi-bar-chart" />Course-wise Performance</h3></div>
                {d.perf.bars.length === 0 ? (
                  <p className="text-muted small mb-0">No data yet.</p>
                ) : (
                  <div className="idb-bars">
                    <div className="idb-bars-y">
                      {[perfMax, (perfMax * 3) / 4, perfMax / 2, perfMax / 4, 0].map((t) => <span key={t}>{Math.round(t)}</span>)}
                    </div>
                    <div className="idb-bars-plot">
                      {d.perf.bars.map((b) => (
                        <div className="idb-bar-col" key={b.name}>
                          <span className="idb-bar-val" style={{ color: b.color }}>{b.value}{d.perf.unit}</span>
                          <div className="idb-bar" style={{ height: `${(b.value / perfMax) * 100}%`, background: b.color }} />
                          <span className="idb-bar-name">{b.name}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </section>

              <section className="idb-card">
                <div className="idb-card-head">
                  <h3><i className="bi bi-file-earmark-check" />Latest Exam Results</h3>
                  <button className="idb-link" onClick={() => navigate("/institute/results")}>View All <i className="bi bi-chevron-right" /></button>
                </div>
                {recentScores.length === 0 ? (
                  <Empty text="No exams attempted yet." sub="Exam results will appear here once students attempt any exams." />
                ) : (
                  <div className="table-responsive">
                    <table className="table align-middle idb-table mb-0">
                      <thead><tr><th>Student</th><th>Course</th><th>Score</th></tr></thead>
                      <tbody>
                        {recentScores.map((r) => (
                          <tr key={r.id}>
                            <td className="fw-semibold">{nameOf(r.userId)}</td>
                            <td>{r.courseName}</td>
                            <td>
                              <span className={`admin-status-pill ${isPass(r.result) ? "active" : "inactive"}`}>
                                
{r.total != null && Number(r.total) > 0
  ? `${r.score}/${r.total} (${percentOf(r)}%)`
  : r.score}

                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>
            </div>
          </>
        )}
      </div>
    </div>
  );
}